import React, { useState, useCallback, useMemo } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, Link } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async'; 
import { Helmet } from 'react-helmet-async';
import './App.css';
import DreamForm from './components/DreamForm';
import LoadingScreen from './components/LoadingScreen';
import DreamResult from './components/DreamResult';
import DreamView from './components/DreamView';
import DreamBackground from './components/DreamBackground';
import ShareModal from './components/ShareModal'; 
import Blog from './components/blog/Blog';
import Lotto from './components/Lotto';
import { ExternalLink, Sparkles } from 'lucide-react';
import { AppContainer, BlogLink, DiscoveryCard, DiscoveryGrid, DiscoveryHeading, DiscoveryIntro, DiscoverySection, HomeActionLink, HomeActionRow, Title, Subtitle, ErrorMessage, TopLinkGroup } from './AppStyles';
import { dreamApiService } from './services/dreamApi';

import Login from './components/admin/Login';
import WritePost from './components/admin/WritePost';
import PrivateRoute from './components/admin/PrivateRoute';

// debounce 유틸리티 함수를 컴포넌트 외부로 이동
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(null, args), wait);
  };
};

function App() {
  const [stage, setStage] = useState('form');
  const [dreamInterpretation, setDreamInterpretation] = useState('');
  const [numberAnalysis, setNumberAnalysis] = useState('');
  const [dreamNumbers, setDreamNumbers] = useState([]);
  const [error, setError] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);  
  const [shareUrl, setShareUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDreamSubmit = useCallback(async (dream) => {
    // 이미 제출 중이면 리턴
    if (isSubmitting) return;

    setIsSubmitting(true);
    setStage('loading');
    setError('');

    try {
      // 네트워크 상태 체크
      if (!navigator.onLine) {
        throw new Error('인터넷 연결을 확인해주세요.');
      }

      const data = await dreamApiService.interpretDream(dream);
      
      // 응답 데이터 유효성 검사
      if (!data || !data.dream_analysis || !data.number_analysis || !data.numbers) {
        throw new Error('올바르지 않은 응답 데이터입니다.');
      }

      setDreamInterpretation(data.dream_analysis);
      setNumberAnalysis(data.number_analysis);
      setDreamNumbers(data.numbers);
      setStage('result');
    } catch (error) {
      console.error('Dream interpretation error:', error);
      // 사용자에게는 친숙한 에러 메시지만 표시
      setError(
        error.message === '인터넷 연결을 확인해주세요.' 
          ? error.message 
          : '서버 오류입니다. 잠시 후 다시 시도해주세요.'
      );
      setStage('form');
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting]);

  const saveAndShare = useCallback(async () => {
    if (!dreamInterpretation || !numberAnalysis || !dreamNumbers.length) return;
  
    try {
      const data = await dreamApiService.saveDream({
        dream_analysis: dreamInterpretation,
        number_analysis: numberAnalysis,
        numbers: dreamNumbers,
      });
      
      if (!data || !data.shareUrl) {
        throw new Error('공유 URL을 생성할 수 없습니다.');
      }
  
      setShareUrl(data.shareUrl);
      setShowShareModal(true);
    } catch (error) {
      console.error('Dream save error:', error);
      setError('저장 요청 실패. 잠시 후 다시 시도해주세요.');
    }
  }, [dreamInterpretation, numberAnalysis, dreamNumbers]);

  const handleSaveAndShare = useMemo(
    () => debounce(saveAndShare, 500),
    [saveAndShare]
  );

  const handleCloseShareModal = useCallback(() => {
    setShowShareModal(false);
    setShareUrl('');
  }, []);

  const handleReturnHome = useCallback(() => {
    // cleanup 함수
    const cleanup = () => {
      setStage('form');
      setDreamInterpretation('');
      setNumberAnalysis('');
      setDreamNumbers([]);
      setError('');
      setShowShareModal(false);
      setShareUrl('');
      setIsSubmitting(false);
    };

    // 즉시 실행하되, 안전하게 처리
    try {
      cleanup();
    } catch (error) {
      console.error('Cleanup error:', error);
      // 강제로 초기 상태로
      window.location.href = '/';
    }
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <DreamBackground />
        <TopLinkGroup aria-label="주요 링크">
          <BlogLink as={Link} to="/lotto">
            <Sparkles size={16} />
            Lotto
          </BlogLink>
          <BlogLink as={Link} to="/blog">
            <ExternalLink size={16} />
            Blog
          </BlogLink>
        </TopLinkGroup>
        <AppContainer>
          {showShareModal && (
            <ShareModal 
              shareUrl={shareUrl}
              onClose={handleCloseShareModal}
            />
          )}
          <Routes>
            <Route path="/rss" element={<Navigate to="/rss.xml" replace />} />
            <Route
              path="/"
              element={
                <>
                  <Helmet>
                    <html lang="ko" />
                    <title>무료 꿈해몽과 행운의 번호 찾기 | 몽픽</title>
                    <meta name="description" content="꿈 내용을 입력하면 의미와 상징을 무료로 해석하고 참고용 행운의 번호까지 알려드려요. 인기 꿈해몽도 함께 확인하세요." />
                    <link rel="canonical" href="https://mongpick.com/" />
                    <meta property="og:title" content="무료 꿈해몽과 행운의 번호 찾기 | 몽픽" />
                    <meta property="og:description" content="어젯밤 꿈의 의미를 무료로 해석하고 참고용 행운의 번호를 확인해보세요." />
                    <meta property="og:url" content="https://mongpick.com/" />
                    <script type="application/ld+json">{JSON.stringify({
                      '@context': 'https://schema.org',
                      '@type': 'WebSite',
                      name: '몽픽',
                      alternateName: 'MongPick',
                      url: 'https://mongpick.com/',
                      description: '꿈의 의미와 상징을 해석하고 참고용 행운의 번호를 제공하는 무료 꿈해몽 서비스'
                    })}</script>
                  </Helmet>
                  <Title>무료 꿈해몽, 몽픽</Title>
                  <Subtitle>꿈의 의미를 풀고 나만의 행운 번호를 Pick하세요.</Subtitle>
                  <HomeActionRow>
                    <HomeActionLink as={Link} to="/lotto" aria-label="몽픽 로또 번호 생성기로 이동">
                      <Sparkles size={18} />
                      로또 번호 생성기
                    </HomeActionLink>
                  </HomeActionRow>
                  {error && <ErrorMessage>{error}</ErrorMessage>}
                  {stage === 'form' && (
                    <DreamForm 
                      onSubmit={handleDreamSubmit}
                      disabled={isSubmitting}
                    />
                  )}
                  {stage === 'loading' && <LoadingScreen />}
                  {stage === 'result' && (
                    <DreamResult 
                      interpretation={dreamInterpretation}
                      numberAnalysis={numberAnalysis}
                      numbers={dreamNumbers}
                      onSaveAndShare={handleSaveAndShare}
                      onReturnHome={handleReturnHome}
                    />
                  )}
                  {stage === 'form' && (
                    <DiscoverySection aria-labelledby="popular-dreams-title">
                      <DiscoveryHeading id="popular-dreams-title">지금 많이 찾는 꿈해몽</DiscoveryHeading>
                      <DiscoveryIntro>비슷한 꿈의 상징과 재물운을 먼저 살펴보세요.</DiscoveryIntro>
                      <DiscoveryGrid>
                        <DiscoveryCard to="/blog/돼지-잡는-꿈-로또번호-및-꿈해몽">
                          <strong>돼지를 잡는 꿈</strong>
                          <span>재물과 성취를 상징하는 꿈의 의미 보기 →</span>
                        </DiscoveryCard>
                        <DiscoveryCard to="/blog/용-집에-들어오는-꿈-로또번호-및-꿈해몽">
                          <strong>용이 집에 들어오는 꿈</strong>
                          <span>가까이 다가온 기회에 관한 꿈의 해석 보기 →</span>
                        </DiscoveryCard>
                        <DiscoveryCard to="/blog/살모사-죽이는-꿈-로또번호-및-꿈해몽">
                          <strong>뱀을 물리치는 꿈</strong>
                          <span>문제 극복과 변화에 관한 꿈의 의미 보기 →</span>
                        </DiscoveryCard>
                      </DiscoveryGrid>
                    </DiscoverySection>
                  )}
                </>
              }
            />
            <Route path="/dream/:dreamId" element={<DreamView />} />
            <Route path="/lotto" element={<Lotto />} />
            <Route path="/lotto/:numbers" element={<Lotto />} />
            <Route path="/blog/*" element={<Blog />} /> 

            {/* 관리자 라우트 추가 */}
            <Route
              path="/admin/dream/login"
              element={<Login />}
            />
            <Route
              path="/admin/dream/write"
              element={
                <PrivateRoute>
                  <WritePost />
                </PrivateRoute>
              }
            />
          </Routes>
        </AppContainer>
      </Router>
    </HelmetProvider>
  );
}

export default App;
